import { useBetterFeatureClient } from "@di-devs/utils-fe";
import { useStore } from "@nanostores/react";
import { useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import type { WritableAtom } from "nanostores";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { EventOccasion } from "../../../shared/types/event.types";
import type {
	AdminCreateOccasionBody,
	AdminUpdateOccasionBody,
	AdminUpdateOccasionsOrderBody,
} from "../../../shared/types/eventApi.types";
import type { EventFeatureClient } from "../../shared/types";

type EventFormValues = AdminCreateOccasionBody | AdminUpdateOccasionBody;

export const useEventAdminManager = () => {
	const queryClient = useQueryClient();
	const client = useBetterFeatureClient<EventFeatureClient>();
	const listEventsResult = client.useListEvents();
	const listCategoriesResult = client.useListCategories();

	const atoms = client.$store.atoms as Record<string, WritableAtom<any>>;
	const selectedEvent = useStore(
		atoms.$selectedEvent as WritableAtom<EventOccasion | null>,
	);
	const isFormOpen = useStore(atoms.$isFormOpen as WritableAtom<boolean>);
	const formMode = useStore(atoms.$formMode as WritableAtom<"create" | "edit">);
	const draftValues = useStore(
		atoms.$draftValues as WritableAtom<Partial<AdminCreateOccasionBody>>,
	);

	const [isSubmitting, setIsSubmitting] = useState(false);

	const events = listEventsResult?.data?.data ?? [];
	const pagination = listEventsResult?.data?.pagination;
	const categories = listCategoriesResult?.data?.data ?? [];

	const isLoading =
		listEventsResult?.isPending || listCategoriesResult?.isPending || false;

	const categoryCount = listCategoriesResult?.data?.data?.length ?? 0;
	const ensureCategoriesLoaded = useCallback(() => {
		if (categoryCount === 0) {
			client.$store.notify("$listCategoriesSignal");
		}
	}, [client, categoryCount]);

	const openCreateForm = useCallback(() => {
		client.openForm("create");
		ensureCategoriesLoaded();
	}, [client, ensureCategoriesLoaded]);

	// Prefill first category when opening create form and categories are available
	useEffect(() => {
		if (
			isFormOpen &&
			formMode === "create" &&
			(categories?.length ?? 0) > 0 &&
			!draftValues?.eventCategoryId
		) {
			const firstCategoryId = categories[0]?.eventCategoryId;
			const current = client.$store.atoms.$draftValues.get() || {};
			client.$store.atoms.$draftValues.set({
				...current,
				eventCategoryId: firstCategoryId,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isFormOpen, formMode, categories]);

	const openEditForm = useCallback(
		async (event: EventOccasion) => {
			client.openForm("edit", event);
			ensureCategoriesLoaded();
			try {
				if (event.eventOccasionId) {
					const detail = await client.getEvent(event.eventOccasionId);
					if (detail && detail.occasion) {
						client.$store.atoms.$selectedEvent.set(detail.occasion);
					}
				}
			} catch (error) {
				console.error("Failed to fetch event details", error);
			}
		},
		[client, ensureCategoriesLoaded],
	);

	const closeForm = useCallback(() => {
		client.closeForm();
		client.resetDraft();
	}, [client]);

	useEffect(() => {
		client.$store.notify("$listEventsSignal");
		client.$store.notify("$listCategoriesSignal");
	}, [client]);

	const handleSubmit = useCallback(
		async (values: EventFormValues) => {
			setIsSubmitting(true);
			try {
				if (formMode === "edit" && selectedEvent?.eventOccasionId) {
					await client.updateEvent(selectedEvent.eventOccasionId, values);
					message.success("Event updated successfully");
				} else {
					await client.createEvent(values);
					message.success("Event created successfully");
				}

				closeForm();
				client.$store.notify("$listEventsSignal");
				queryClient.invalidateQueries({ queryKey: ["eventOccasionsList"] });
			} catch (error) {
				console.error(error);
				message.error("Failed to save event");
			} finally {
				setIsSubmitting(false);
			}
		},
		[client, closeForm, formMode, selectedEvent?.eventOccasionId],
	);

	const handleDelete = useCallback(
		async (eventOccasionId: number) => {
			try {
				await client.deleteEvent(eventOccasionId);
				message.success("Event deleted");
				client.$store.notify("$listEventsSignal");
				queryClient.invalidateQueries({ queryKey: ["eventOccasionsList"] });
			} catch (error) {
				console.error(error);
				message.error("Failed to delete event");
			}
		},
		[client],
	);

	const handleUpdateOrder = useCallback(
		async (occasions: EventOccasion[]) => {
			setIsSubmitting(true);
			try {
				await client.updateEventsOrder({
					occasions: occasions.map((o, i) => ({
						eventOccasionId: (o.eventOccasionId || (o as any).id) as number,
						displayOrder: i,
					})),
				});
				message.success("表示順を更新しました");
				client.$store.notify("$listEventsSignal");
				queryClient.invalidateQueries({ queryKey: ["eventOccasionsList"] });
			} catch (error) {
				console.error(error);
				message.error("表示順の更新に失敗しました");
			} finally {
				setIsSubmitting(false);
			}
		},
		[client],
	);

	const formInitialValues = useMemo<Partial<AdminCreateOccasionBody>>(() => {
		if (formMode === "edit" && selectedEvent) {
			return {
				eventCategoryId: selectedEvent.eventCategoryId,
				title: selectedEvent.title,
				subtitle: selectedEvent.subtitle ?? undefined,
				description: selectedEvent.description ?? undefined,
				membershipScope: selectedEvent.membershipScope,
				allowNonMember: selectedEvent.allowNonMember,
				isPaidEvent: selectedEvent.isPaidEvent,
				price: selectedEvent.price ?? undefined,
				currency: selectedEvent.currency ?? undefined,
				isPublished: selectedEvent.isPublished,
				reservationStartAt: selectedEvent.reservationStartAt ?? undefined,
				reservationEndAt: selectedEvent.reservationEndAt ?? undefined,
				allowMultipleBookings: selectedEvent.allowMultipleBookings,
				groupBookingMode: selectedEvent.groupBookingMode,
				displayOrder: selectedEvent.displayOrder,
				fee: selectedEvent.fee,
				cancelable: selectedEvent.cancelable,
				cancelConditions: selectedEvent.cancelConditions,
				cancelDescription: selectedEvent.cancelDescription ?? undefined,
				isMessage: selectedEvent.isMessage,
				messageRegisteredLineWorks:
					selectedEvent.messageRegisteredLineWorks ?? undefined,
				messageCanceledLineWorks:
					selectedEvent.messageCanceledLineWorks ?? undefined,
				categoryMessageDetail: selectedEvent.categoryMessageDetail,
				emailsLineWorks: selectedEvent.emailsLineWorks,
				heroImageUrl: selectedEvent.heroImageUrl ?? undefined,
				gallery: selectedEvent.gallery,
				isMultiEvent: selectedEvent.isMultiEvent,
				notRegisterEventSameTime: selectedEvent.notRegisterEventSameTime,
			};
		}

		return draftValues;
	}, [draftValues, formMode, selectedEvent]);

	return {
		events,
		pagination,
		categories,
		isLoading,
		isSubmitting,
		isFormOpen,
		formMode,
		selectedEvent,
		formInitialValues,
		openCreateForm,
		openEditForm,
		closeForm,
		handleSubmit,
		handleDelete,
		handleUpdateOrder,
		refresh: () => client.$store.notify("$listEventsSignal"),
	};
};
